<template>
    <div>
        <b-datatable
            :key="datatableKey"
            :columns="columns"
            :endpoint="listRoute"
            :hidden-columns="hiddenColumns"
            :initial-filters="initialFilters"
            has-filter-selector
            item-key="id"
            qc-mode
        >
            <template #buttons>
                <div class="px-2">
                    <v-tooltip top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                v-if="
                                    can('api.v1.laboratories.store') &&
                                    can('api.v1.collection-centres.store') &&
                                    can('providers.create')
                                "
                                color="primary"
                                icon
                                v-bind="attrs"
                                :href="createItemUrl()"
                                v-on="on"
                            >
                                <v-icon>mdi-plus-circle</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('blood-providers::button.create') }}</span>
                    </v-tooltip>
                </div>
            </template>
            <template #filters="{ filters }">
                <v-col cols="4" class="pb-0">
                    <b-filter-input
                        v-model="filters.filters.name"
                        :label="t('blood-providers::grid.label.name')"
                    ></b-filter-input>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-input
                        v-model="filters.filters.dms_id"
                        :label="t('blood-providers::grid.label.dms-id')"
                        type="number"
                    ></b-filter-input>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.is_registered"
                        :items="registeredFilterItems"
                        :label="t('blood-providers::grid.label.is_active')"
                        item-text="label"
                        item-value="value"
                        :clearable="filters.filters.is_registered !== null"
                        :placeholder="t('blood-providers::grid.label.is_active')"
                    >
                    </b-filter-select>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.type"
                        :items="typeFilterItems"
                        item-text="label"
                        :label="t('blood-providers::grid.label.type')"
                        item-value="value"
                        :clearable="filters.filters.type !== null"
                        :placeholder="t('blood-providers::grid.label.type')"
                    >
                    </b-filter-select>
                </v-col>
            </template>

            <template #[`item.is_registered`]="{ item }">
                <div class="grid-status">
                    <span>
                        {{ item.is_registered ? t('default::label.yes') : t('default::label.no') }}
                    </span>
                </div>
            </template>

            <template #[`item.name`]="{ item }">
                <div v-if="can('providers.get')" class="link" @click="$root.goTo(editItemUrl(item))">
                    {{ item.name }}
                </div>
                <div v-else>
                    {{ item.name }}
                </div>
            </template>

            <template #[`item.is_patient_mode`]="{ item }">
                <div v-if="!item.is_patient_mode && item.visible_in_qc_list">
                    <v-icon color="red">mdi-alert-octagon-outline</v-icon>
                    <span class="error--text">{{ t('blood-providers::status-qc-off') }}</span>
                </div>
            </template>

            <template #[`item.type`]="{ item }">
                {{ providersTypes[item.type] }}
            </template>
            <template #[`item.actions`]="{ item }">
                <div class="table-actions">
                    <v-tooltip v-if="can('providers.export-record-as-pdf')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="exportRecordAsPdf(item)"
                            >
                                <v-icon size="24">mdi-file-export-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.export_record_as_pdf') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="can('api.v1.laboratories.update') || can('api.v1.collection-centres.update')"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(editItemUrl(item))"
                            >
                                <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('blood-providers::button.edit') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="
                            !(can('api.v1.laboratories.update') || can('api.v1.collection-centres.update')) &&
                            can('api.v1.providers.show')
                        "
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(editItemUrl(item))"
                            >
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('blood-providers::button.details') }}</span>
                    </v-tooltip>

                    <b-delete
                        v-if="
                            deleteItemUrl(item) &&
                            (can('api.v1.collection-centres.destroy') || can('api.v1.laboratories.destroy'))
                        "
                        :path="deleteItemUrl(item)"
                        :hide-true-btn="!!item.pharmacy_site"
                        :text="
                            !!item.pharmacy_site
                                ? t('blood-providers::confirmation.assigned_to_pharmacy_site_content')
                                : t('default::confirmation.deletion')
                        "
                        :title="
                            !!item.pharmacy_site
                                ? t('blood-providers::confirmation.assigned_to_pharmacy_site_title')
                                : t('default::confirmation.general')
                        "
                        icon="mdi-trash-can"
                        @deleted="triggerDeleted"
                    >
                    </b-delete>
                </div>
            </template>
        </b-datatable>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            downloadingFile: false,
            initialFilters: {
                type: 'cc,lab',
                is_registered: null,
            },
            providersTypes: {
                cc: 'Collection Centre',
                lab: 'Laboratory',
            },
            hiddenColumns: ['id'],
            typeFilterItems: [
                { label: t('default::label.all'), value: 'cc,lab' },
                { label: t('blood-providers::label.cc'), value: 'cc' },
                { label: t('blood-providers::label.lab'), value: 'lab' },
            ],
            registeredFilterItems: [
                { label: t('default::label.all'), value: null },
                { label: t('blood-providers::label.yes'), value: 1 },
                { label: t('blood-providers::label.no'), value: 0 },
            ],
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('blood-providers::grid.label.name'),
                    width: 200,
                    value: 'name',
                    sortable: true,
                },
                {
                    text: null,
                    value: 'is_patient_mode',
                    sortable: false,
                    class: 'is_patient_mode',
                    cellClass: 'is_patient_mode',
                    width: 0,
                },
                {
                    text: t('pharmacy-groups::label.registered'),
                    value: 'is_registered',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('blood-providers::grid.label.dms-id'),
                    value: 'dms_id',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('blood-providers::grid.label.type'),
                    value: 'type',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: 'pharmacy_site.id,is_patient_mode,visible_in_qc_list',
                    width: 180,
                    align: 'end',
                },
            ],
        };
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('providers.export-record-as-pdf', { blood_result_provider: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        deleteItemUrl(item) {
            switch (item.type) {
                case 'cc':
                    return route('api.v1.collection-centres.destroy', { collection_centre: item.id });
                case 'lab':
                    return route('api.v1.laboratories.destroy', { laboratory: item.id });
                default:
                    return false;
            }
        },
        triggerDeleted() {
            this.datatableKey++;
            this.$root.notify(
                'info',
                t('default::notification.success'),
                t('blood-providers::notification.blood_providers.deleted')
            );
            this.$store.dispatch('qualityControl/fetchBlockedDeviceList');
        },
        createItemUrl() {
            return route('providers.create');
        },
        editItemUrl(item) {
            return route('providers.get', { blood_result_provider: item.id, type: item.type });
        },
    },
};
</script>
